/**
 * App Icon Badge Service — Unified Notification Counter
 *
 * The app icon badge = CRM Lead Sessions (unread) + Feedback (unread) + Stock Notifications (unread)
 * Order chat is intentionally excluded.
 *
 * Architecture: All notification sources are aggregated by the backend at
 * GET /api/crm/notifications/summary so that any future source just needs to be
 * added there — the frontend never has to know about individual sources.
 *
 * Supported platforms: Android (Chrome 81+), iOS 16.4+ (as PWA), Windows Chrome/Edge.
 */
import api from './api';

const canBadge = () => 'setAppBadge' in navigator;

/** Set badge to exact count. Pass 0 to clear. */
export const setBadge = async (count) => {
  if (!canBadge()) return;
  try {
    if (count > 0) await navigator.setAppBadge(count);
    else await navigator.clearAppBadge();
  } catch {}
};

/** Clear badge completely. */
export const clearBadge = async () => {
  if (!canBadge()) return;
  try { await navigator.clearAppBadge(); } catch {}
};

/**
 * Fetch the unified notification summary and update both the app icon badge
 * and the browser tab title.
 * Returns the full summary object so callers can update per-menu counts.
 */
export const refreshBadge = async () => {
  try {
    const res = await api.get('/crm/notifications/summary');
    const { total = 0, crm_sessions = 0, feedback = 0, stock_notifications = 0, diamond_pending = 0 } = res.data;
    await setBadge(total);
    updateTabTitle(total);
    return { total, crm_sessions, feedback, stock_notifications, diamond_pending };
  } catch {
    // Non-CRM roles will get 403 — silently ignore, they don't need the badge
    return { total: 0, crm_sessions: 0, feedback: 0, stock_notifications: 0, diamond_pending: 0 };
  }
};

/** Update browser tab title with unread count. */
export const updateTabTitle = (count) => {
  const base = 'DiaForge';
  document.title = count > 0 ? `(${count}) ${base}` : base;
};
